import { AxiosError } from 'axios'
import { sanitizeHtml } from 'conversations/utils/htmlUtils'
import { findIndexByPresendUUID, getPresendUUID } from 'conversations/utils/presend'
import produce from 'immer'
import unionBy from 'lodash/unionBy'
import { combineReducers } from 'redux'
import {
  deleteMessageAction,
  fetchAttachmentsAsync,
  fetchMessagesAsync,
  putMessage,
  receiveMessage,
  removeMessage,
  sendMessageAsync,
} from 'shared/store/actions/message'
import { Pagination } from 'shared/typings/api/PaginatedResponse'
import { Attachment } from 'shared/typings/conversations/Attachment'
import { ConversationStatus } from 'shared/typings/conversations/status/ConversationStatus'
import { Message } from 'shared/typings/messages/Message'

import { createReducer, isActionOf, RootAction } from 'typesafe-actions'

export type MessagesByConversationId = Record<number, Message[] | undefined>
export type PaginationByConversationId = Record<number, Pagination | undefined>
export type StatusByConversationId = Record<number, string | undefined>
export type ErrorByConversationId = Record<number, AxiosError | undefined>
export type AttachmentsByMessageId = Record<number, Attachment[]>

const byConversationId = createReducer<MessagesByConversationId, RootAction>({})
  .handleAction(deleteMessageAction, (state, { payload: message }) => {
    return produce<RootAction, MessagesByConversationId>(state, (draft) => {
      const presendUUID = getPresendUUID(message)
      const messages = draft[message.conversationId]
      const presendIndex = findIndexByPresendUUID(messages, presendUUID)

      if (presendIndex > -1 && messages) {
        messages.splice(presendIndex, 1)
      }
    })
  })
  .handleAction(sendMessageAsync.request, (state, { payload: message }) => {
    return produce<RootAction, MessagesByConversationId>(state, (draft) => {
      const presendUUID = getPresendUUID(message)
      const messages = draft[message.conversationId]
      const presendIndex = findIndexByPresendUUID(messages, presendUUID)

      // If the message is present it means we are resending
      if (presendIndex > -1 && messages) {
        messages[presendIndex] = { ...message }
      } else if (messages !== undefined) {
        messages.push(message)
      } else {
        draft[message.conversationId] = [message]
      }
    })
  })
  .handleAction(sendMessageAsync.failure, (state, { payload: { message } }) => {
    return produce<RootAction, MessagesByConversationId>(state, (draft) => {
      const presendUUID = getPresendUUID(message)
      const messages = draft[message.conversationId]
      const presendIndex = findIndexByPresendUUID(messages, presendUUID)

      if (presendIndex > -1 && messages) {
        messages[presendIndex].id = -1
      }
    })
  })
  .handleAction(
    [receiveMessage, sendMessageAsync.success, putMessage],
    (state, { payload: message }: { payload: Message }) => {
      return produce<RootAction, MessagesByConversationId>(state, (draft) => {
        const messages: Message[] | undefined = draft[message.conversationId]
        if (messages !== undefined) {
          const presendUUID = getPresendUUID(message)
          const presendIndex = findIndexByPresendUUID(messages, presendUUID)
          const messageIndex = messages.findIndex((m) => message.id && m.id === message.id)

          // If we have a presendUUID, ignore the UUID coming from the backend
          // because we key off of uuid for rendering and we'll re-animate it in
          const newMessage = produce(message, (draft) => {
            draft.uuid = presendUUID && draft.uuid !== presendUUID ? presendUUID : draft.uuid

            // We can't trust HTML sent from server
            draft.body = sanitizeHtml(draft.body)
          })

          // If the present message exists, replace it
          if (messageIndex > -1) {
            messages[messageIndex] = newMessage
          } else if (presendIndex > -1) {
            messages[presendIndex] = newMessage
          } else {
            messages.push(newMessage)
          }
        } else {
          draft[message.conversationId] = [message]
        }
      })
    }
  )
  .handleAction(
    fetchMessagesAsync.success,
    (
      state,
      {
        payload: {
          paginatedMessages: { data: messages },
          conversationId,
        },
      }
    ) => {
      if (messages.length === 0) {
        return state
      }

      return produce(state, (draft) => {
        draft[conversationId] = unionBy(
          messages
            .filter(
              (message: Message) =>
                message?.conversationEvent?.conversationStatus !== ConversationStatus.PENDING
            )
            .filter(
              (message: Message) =>
                message?.conversationEvent?.conversationStatus !== ConversationStatus.ARCHIVED
            )
            .map((message: Message) => ({ ...message, body: sanitizeHtml(message.body) })),
          draft[conversationId],
          'id'
        ).sort((a, b) => a.createdAt - b.createdAt)
      })
    }
  )
  .handleAction(removeMessage, (state, { payload: { conversationId, messageUuid } }) => {
    return produce<RootAction, MessagesByConversationId>(state, (draft) => {
      const index = draft[conversationId]?.findIndex((m) => m.uuid === messageUuid)
      const messages = draft[conversationId]
      if (messages && index && index > -1) {
        messages.splice(index, 1)
      }
    })
  })

const paginationByConversationId = createReducer<PaginationByConversationId, RootAction>(
  {}
).handleAction(
  fetchMessagesAsync.success,
  (
    state,
    {
      payload: {
        paginatedMessages: { data: messages, pagination },
        conversationId,
      },
    }
  ) => {
    if (messages.length === 0) {
      return state
    }

    // Sometimes we will re-fetch the latest messages, don't update pagination in those cases
    const prevNext = Number(state[conversationId]?.next || 0)
    const newNext = Number(pagination?.next || 0)
    if (newNext > prevNext && state[conversationId] !== undefined) {
      return state
    }

    return produce(state, (draft) => {
      draft[conversationId] = pagination
    })
  }
)

const fetchedInitialListByConversationId = createReducer<Record<number, boolean | undefined>>(
  {}
).handleAction(fetchMessagesAsync.success, (state, { payload: { conversationId } }) => {
  return produce(state, (draft) => {
    draft[conversationId] = true
  })
})

const statusByConversationId = createReducer<StatusByConversationId, RootAction>({}).handleAction(
  [fetchMessagesAsync.request, fetchMessagesAsync.success, fetchMessagesAsync.failure],
  (state, action) =>
    produce(state, (draft) => {
      if (isActionOf(fetchMessagesAsync.request, action) && action.payload.loadMore) {
        draft[action.payload.conversationId] = `${action.type}_LOADING_MORE`
      } else if (
        isActionOf(fetchMessagesAsync.request, action) &&
        state[action.payload.conversationId] === undefined
      ) {
        draft[action.payload.conversationId] = `${action.type}_INITIAL_LOAD`
      } else {
        draft[action.payload.conversationId] = action.type
      }
    })
)

const errorByConversationId = createReducer<ErrorByConversationId, RootAction>({}).handleAction(
  [fetchMessagesAsync.failure, fetchMessagesAsync.success],
  (state, action) =>
    produce(state, (draft) => {
      if (isActionOf(fetchMessagesAsync.success, action)) {
        delete draft[action.payload.conversationId]
      }
      draft[action.payload.conversationId] = action.payload.error
    })
)

const attachmentsByMessageId = createReducer<AttachmentsByMessageId, RootAction>({}).handleAction(
  [fetchAttachmentsAsync.success],
  (state, action) => {
    return produce(state, (draft) => {
      draft[action.payload.messageId] = action.payload.attachments
    })
  }
)

export default combineReducers({
  byConversationId,
  paginationByConversationId,
  statusByConversationId,
  fetchedInitialListByConversationId,
  attachmentsByMessageId,
  errorByConversationId,
})
